import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, Panel, Pill, Avatar } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import { Baby, ChalkboardTeacher, Sparkle, NotePencil, ForkKnife, ChartLineUp } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [meals, setMeals] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, u, m] = await Promise.all([
          api.get("/students"),
          api.get("/daily-updates"),
          api.get("/meals"),
        ]);
        setStudents(s.data);
        setUpdates(u.data);
        setMeals(m.data);
      } catch (e) { console.error(e); }
    })();
  }, [user]);

  const today = new Date().toISOString().slice(0, 10);
  const todayMeal = meals.find((m) => m.date === today);
  const updatesToday = updates.filter((u) => u.date === today);

  return (
    <div>
      <PageHeader
        eyebrow="Teacher"
        title={`Hello ${user?.name?.split(" ")[0] || "there"} 🌼`}
        subtitle="Today's class, daily updates, and gentle AI to keep your day light."
        action={
          <Link to="/app/ai" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-semibold hover:bg-black/90 transition" data-testid="teacher-ai-link">
            <Sparkle weight="duotone" /> Ask MangoCubs AI
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="My class" value={students.length} icon={Baby} accent="terracotta" />
        <StatCard label="Updates today" value={updatesToday.length} icon={NotePencil} accent="lavender" />
        <StatCard label="Updates this week" value={updates.length} icon={ChartLineUp} accent="sage" />
        <StatCard label="Today's lunch" value={todayMeal?.lunch ? "Ready" : "—"} icon={ForkKnife} accent="mustard" hint={todayMeal?.lunch || "Menu not posted"} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="My class today" className="lg:col-span-2" action={<Link to="/app/attendance" className="text-sm text-terracotta font-semibold">Mark attendance →</Link>}>
          <div className="grid sm:grid-cols-2 gap-3">
            {students.slice(0, 12).map((s) => (
              <Link to={`/app/students`} key={s.student_id} className="flex items-center gap-3 p-3 rounded-2xl border border-border hover:bg-paper-alt transition">
                <Avatar name={s.name} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.name}</div>
                  <div className="text-xs text-ink-soft">{s.age_group} · {s.class_name}</div>
                </div>
                <Pill tone="sage">{s.status}</Pill>
              </Link>
            ))}
          </div>
        </Panel>

        <Panel title="Recent updates" action={<Link to="/app/daily-updates" className="text-sm text-terracotta font-semibold">All updates →</Link>}>
          <div className="space-y-3">
            {updates.slice(0, 6).map((u) => {
              const s = students.find(x => x.student_id === u.student_id);
              return (
                <div key={u.update_id} className="p-3 rounded-2xl bg-paper-alt">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{s?.name || "Student"}</div>
                    <Pill tone="mustard">{u.mood}</Pill>
                  </div>
                  <div className="text-xs text-ink-soft mt-1">{u.date}</div>
                  {u.activities?.[0] && <div className="text-sm mt-2">• {u.activities[0]}</div>}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}
